import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { CatDrawer } from "blocks-nextjs/blocks-react/components/Drawer";
import { CatHeading } from "blocks-nextjs/blocks-react/components/Heading/Heading";
import { CatIconArrowBack } from "blocks-nextjs/blocks-react/components/Icons/ArrowBack/ArrowBack";
import React, { useEffect, useState } from "react";
import { useBodyScrollLock } from "../../hooks/useBodyScrollLock";
import EssPageDetailV2 from "../../pages/Projects/EssPageDetailV2";
import useProjectDetailsStore from "../../store/useProjectDetailsStore";
const DRAWER_WIDTH = "75%";
const BACK_BUTTON_CLASSES = "flex items-center gap-2 cursor-pointer hover:opacity-80";
const BACK_ICON_CLASSES = "w-5 h-5 mt-1";
/**
 * ESS Asset Detail Drawer v2 shell.
 *
 * Structurally and visually aligned with the Charger Asset Detail Drawer
 * (ChargerAssetDrawer): same CatDrawer configuration, width, header slot and
 * body-scroll locking. Rendered in place of the legacy ESS drawer only when
 * the `showEssDrawerV2` feature flag is enabled (see AssetPortfolio).
 */
const EssAssetDrawerV2 = ({ assetDrawerActive, onAssetSelect, setAssetDrawerActive }) => {
    const { assetDetails } = useProjectDetailsStore((state) => ({
        assetDetails: state.assetDetails
    }));
    const [isRefreshComponent, setIsRefreshComponent] = useState(assetDrawerActive);
    const [showGenerateLink, setShowGenerateLink] = useState(false);
    const [showChargeHistory, setShowChargeHistory] = useState(false);
    // Lock body scroll when the drawer or any sub-view is active (matches
    // charger drawer). A single combined call avoids the module-level refcount
    // briefly leaving the body locked after close and covers the Generate Link
    // sub-view too.
    useBodyScrollLock(assetDrawerActive || showGenerateLink || showChargeHistory);
    const handleBackClick = () => {
        setShowGenerateLink(false);
        setShowChargeHistory(false);
    };
    useEffect(() => {
        setIsRefreshComponent(assetDrawerActive);
        // Reset the sub-views when the drawer closes so it always reopens on the
        // main asset-details view (matches charger drawer).
        if (!assetDrawerActive) {
            setShowGenerateLink(false);
            setShowChargeHistory(false);
        }
    }, [assetDrawerActive]);
    const renderHeaderTitle = () => {
        // Match the page body's precedence (EssPageDetailV2 checks showGenerateLink
        // before showChargeHistory) so the header and body always agree on the
        // active sub-view, even if both flags were ever true in controlled mode.
        if (showGenerateLink) {
            return (_jsxs("button", { className: BACK_BUTTON_CLASSES, onClick: handleBackClick, type: "button", children: [_jsx(CatIconArrowBack, { className: BACK_ICON_CLASSES }), _jsx("span", { children: "Back" })] }));
        }
        if (showChargeHistory) {
            return (_jsxs("button", { className: BACK_BUTTON_CLASSES, onClick: handleBackClick, type: "button", children: [_jsx(CatIconArrowBack, { className: BACK_ICON_CLASSES }), _jsx("span", { children: "Transaction History" })] }));
        }
        return "Asset Details";
    };
    return (_jsx(CatDrawer, { className: "custom-drawer [--cat-icon-fill:var(--Content-Default,#3F3F3F)] [--cat-icon-height:13px] [--cat-icon-width:13px]", "data-test": "ess-asset-details-drawer-v2", disableBackdropClick: false, disableEscClick: true, hasBackdrop: true, isActive: assetDrawerActive, 
        // isInline=false so CatDrawer uses the native <dialog>.showModal() top-layer: its
        // ::backdrop then covers the full viewport and outside clicks resolve to the
        // dialog itself, which is what CatDrawer's own click-outside check requires to
        // close. isInline=true skips showModal(), so the dialog only spans the drawer's
        // own (75%) width and clicks on the remaining page area never reach it.
        isInline: false, onBlDrawerClose: () => {
            setAssetDrawerActive(false);
            if (assetDetails) {
                onAssetSelect(assetDetails);
            }
        }, onBlDrawerOpen: () => { }, preserveContent: true, width: DRAWER_WIDTH, children: _jsxs(_Fragment, { children: [_jsx("div", { className: "custom-drawer-header", "data-test": "ess-asset-details-drawer-v2-header", slot: "header", children: _jsx(CatHeading, { "data-test": "ess-asset-details-drawer-v2-title", level: "h1", variant: "title-sm", children: renderHeaderTitle() }) }), _jsx(EssPageDetailV2, { isRefreshComponent: isRefreshComponent, onChargeHistoryStateChange: setShowChargeHistory, onGenerateLinkStateChange: setShowGenerateLink, showChargeHistory: showChargeHistory, showGenerateLink: showGenerateLink })] }) }));
};
export default EssAssetDrawerV2;
